import PropTypes from 'prop-types';

/**
 * Button component showcasing Pawfect Match theme system
 * Uses CSS custom properties and utility classes
 */
function Button({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon = null,
  onClick,
  className = '',
  ...props
}) {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'rounded-lg',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'cursor-pointer',
  ];

  const variantClasses = {
    primary: [
      'bg-primary-600',
      'text-white',
      'hover:bg-primary-700',
      'focus:ring-primary-500',
      'dark:bg-primary-500',
      'dark:hover:bg-primary-600',
      'shadow',
      'hover:shadow-md',
    ],
    secondary: [
      'bg-amber-600',
      'text-white',
      'hover:bg-amber-700',
      'focus:ring-amber-500',
      'dark:bg-amber-600',
      'dark:hover:bg-amber-700',
      'dark:text-white',
      'shadow',
      'hover:shadow-md',
    ],
    outline: [
      'bg-white',
      'text-neutral-900',
      'border-2',
      'border-primary-600',
      'hover:bg-primary-600',
      'hover:text-white',
      'focus:ring-primary-500',
      'dark:bg-neutral-800',
      'dark:text-neutral-100',
      'dark:border-primary-400',
      'dark:hover:bg-primary-600',
      'dark:hover:text-white',
      'shadow-sm',
      'hover:shadow',
    ],
    ghost: [
      'bg-transparent',
      'text-neutral-900',
      'hover:bg-neutral-100',
      'focus:ring-primary-500',
      'dark:text-neutral-100',
      'dark:hover:bg-neutral-700',
    ],
    success: [
      'bg-green-600',
      'text-white',
      'hover:bg-green-700',
      'focus:ring-green-500',
      'dark:bg-green-500',
      'dark:hover:bg-green-600',
      'shadow',
      'hover:shadow-md',
    ],
    warning: [
      'bg-amber-600',
      'text-white',
      'hover:bg-amber-700',
      'focus:ring-amber-500',
      'dark:bg-amber-600',
      'dark:hover:bg-amber-700',
      'shadow',
      'hover:shadow-md',
    ],
    error: [
      'bg-red-600',
      'text-white',
      'hover:bg-red-700',
      'focus:ring-red-500',
      'dark:bg-red-500',
      'dark:hover:bg-red-600',
      'shadow',
      'hover:shadow-md',
    ],
  };

  const sizeClasses = {
    small: ['px-3', 'py-2', 'text-sm', 'gap-1'],
    medium: ['px-4', 'py-3', 'text-base', 'gap-2'],
    large: ['px-6', 'py-4', 'text-lg', 'gap-3'],
  };

  const disabledClasses = disabled
    ? ['opacity-50', 'cursor-not-allowed', 'pointer-events-none']
    : ['transform', 'hover:scale-105'];

  const loadingClasses = loading ? ['opacity-75', 'cursor-wait'] : [];

  const allClasses = [
    ...baseClasses,
    ...variantClasses[variant],
    ...sizeClasses[size],
    ...disabledClasses,
    ...loadingClasses,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={allClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {icon && !loading && <span className="button-icon">{icon}</span>}
      {children}
    </button>
  );
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'outline',
    'ghost',
    'success',
    'warning',
    'error',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default Button;